"use client";

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/components/ui/utils';

export interface SegmentedOption {
  id: string;
  label: string;
}

interface SegmentedControlProps {
  options: SegmentedOption[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  itemClassName?: string;
  ariaLabel?: string;
}

/**
 * 단일선택 세그먼트(필터) 공용 컴포넌트. Radix ToggleGroup type="single" 기반으로
 * role="radiogroup"/role="radio"/aria-checked 를 제공한다(목록 필터는 탭이 아니므로 role="tab" 미사용).
 * 활성 스타일은 언더라인 탭(하단 보더) 단일 패턴. 시각은 탭처럼 보이되 ARIA 는 단일선택 필터로 올바름.
 * 레이아웃은 className 으로 호출부가 제어.
 */
export function SegmentedControl({
  options,
  value,
  onValueChange,
  className,
  itemClassName,
  ariaLabel,
}: SegmentedControlProps) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      // Radix 는 활성 재클릭 시 빈문자열을 전달 → 무시(필터는 항상 1개 선택 유지)
      onValueChange={(v) => {
        if (v) {
          onValueChange(v);
        }
      }}
      // Radix 기본 root role 은 "group" 이고 item 은 role="radio" → radio 는 radiogroup 안에 있어야
      // 유효한 ARIA. 명시로 정정(+ getByRole('radiogroup') 결정적).
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn('flex', className)}
    >
      {options.map((opt) => (
        <ToggleGroupItem
          key={opt.id}
          value={opt.id}
          // 언더라인 탭: 투명 배경 + 하단 보더 인디케이터. toggleVariants 기본(bg-accent active/hover:bg-muted/
          // hover:text-muted-foreground/rounded-md/h-9)을 transparent·rounded-none·상태별 specificity 로 결정적 중화.
          // first/last:rounded-none 은 ToggleGroupItem 기본 first:rounded-l-md/last:rounded-r-md 중화(twMerge same-variant).
          // h-auto 로 padding 기반 높이 제어. ARIA(radio)는 불변 — 시각만 탭룩.
          className={cn(
            'h-auto rounded-none first:rounded-none last:rounded-none border-b-2 border-transparent bg-transparent py-2 text-sm font-medium transition-colors',
            'data-[state=on]:border-primary data-[state=on]:bg-transparent data-[state=on]:text-foreground data-[state=on]:hover:bg-transparent data-[state=on]:hover:text-foreground',
            'data-[state=off]:border-transparent data-[state=off]:bg-transparent data-[state=off]:text-muted-foreground data-[state=off]:hover:bg-transparent data-[state=off]:hover:text-foreground',
            itemClassName,
          )}
        >
          {opt.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
